import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthInterceptor } from '@core/auth/auth.interceptor';
import { AuthService } from '@core/auth/auth.service';
import { AccountSelectionInterceptor } from '@core/interceptors/account-selection.interceptor';
import { RetryInterceptor } from '@core/interceptors/retry.interceptor';
import { TimeoutInterceptor } from '@core/interceptors/timeout.interceptor';
import { ExpansionPanelModule } from '@shared/modules/expansion-panel/expansion-panel.module';
import { ToastMessageModule } from '@shared/modules/toast-message/toast-message.module';
import { ApiModule, Configuration } from '@wtax/data-angular';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { SelectModule } from '@shared/modules/select/select.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import * as icons from '../assets/icons';
import { environment } from '../environments/environment';
import { Config, CONFIG_TOKEN } from '../wtax-config';
import * as fromAccountSelection from '../app/components/header/account-selection/reducers';
import { AccountSelectionEffects } from '../app/components/header/account-selection/effects/account-selection-effects.service';
import * as fromDocumentRequestBatches from './modules/document-request-batches/reducers';
import { DocumentRequestBatchEffects } from './modules/document-request-batches/effects/document-request-batch.effects';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCallbackComponent } from './components/auth-callback/auth-callback.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { NavigationItemComponent } from './components/header/navigation-item.component';
import { CoreModule } from './core/core.module';
import { MockInterceptor } from './mock/mock.interceptor';
import { SharedModule } from './shared/shared.module';
import { AccountDropdownComponent } from './components/header/account-selection/account-dropdown.component';

@NgModule({
  declarations: [AppComponent, HeaderComponent, AccountDropdownComponent, NavigationItemComponent, AuthCallbackComponent, FooterComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    HttpClientModule,
    ToastMessageModule.forRoot(),
    BrowserAnimationsModule,
    CoreModule,
    ExpansionPanelModule,
    ApiModule,
    SelectModule,
    StoreModule.forFeature(fromAccountSelection.accountSelectionFeatureKey, fromAccountSelection.reducers),
    EffectsModule.forFeature([AccountSelectionEffects]),
    // Registered here, not in DocumentOverviewModule, because both the Document Manager and the
    // single-request lazy modules use this state and must not register it once each.
    StoreModule.forFeature(fromDocumentRequestBatches.documentRequestBatchesFeatureKey, fromDocumentRequestBatches.reducers),
    EffectsModule.forFeature([DocumentRequestBatchEffects]),
  ],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
      },
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
      },
    },
    {
      provide: Configuration,
      useFactory: (config: Config, authService: AuthService) =>
        new Configuration({
          basePath: config.apiBaseUrl,
          accessToken: authService.getAccessToken(),
        }),
      deps: [CONFIG_TOKEN, AuthService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccountSelectionInterceptor,
      multi: true,
    },
    // enable mocking based on environment configuration
    environment.debug
      ? [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: MockInterceptor,
            multi: true,
          },
        ]
      : [],
    // intercepting failed responses and retrying them
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  exports: [NavigationItemComponent, FooterComponent],
})
export class AppModule {
  // Use SvgIconsRegistry to register icons
  constructor(private svgIconsRegistry: SvgIconRegistry) {
    this.svgIconsRegistry.register(Object.values(icons)); // Register your icons here
  }
}
